import { FormEvent, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";
import FormField from "../components/FormField";
import GoogleSignInButton from "../components/GoogleSignInButton";
import MicrosoftSignInButton from "../components/MicrosoftSignInButton";
import { ApiError } from "../api/client";
import type { ValidationDetail } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { trackEvent } from "../utils/analytics";

interface FieldErrors { email?: string; password?: string; confirm?: string }

function fieldErrorsFromDetails(details: ValidationDetail[] | null): FieldErrors {
  const out: FieldErrors = {};
  if (!details) return out;
  for (const d of details) {
    const name = d.loc[d.loc.length - 1];
    if (name === "email" && !out.email) out.email = d.msg;
    if (name === "password" && !out.password) out.password = d.msg;
  }
  return out;
}

export default function Signup() {
  const { user, loading, signup } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  function validateClient(em: string, pw: string, cf: string): FieldErrors {
    const errors: FieldErrors = {};
    if (!em.trim()) errors.email = t("validation.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) errors.email = t("validation.emailInvalid");
    if (!pw) errors.password = t("validation.passwordRequired");
    else if (pw.length < 8) errors.password = t("validation.passwordMinLength");
    if (pw !== cf) errors.confirm = t("validation.passwordsNoMatch");
    return errors;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    const clientErrors = validateClient(email, password, confirm);
    setFieldErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    setSubmitting(true);
    try {
      await signup(email, password);
      trackEvent("sign_up", { method: "email" });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 || err.code === "EMAIL_TAKEN") { setFieldErrors({ email: t("signup.emailTaken") }); setFormError(t("signup.emailTakenForm")); }
        else if (err.status === 422) { setFieldErrors(fieldErrorsFromDetails(err.details)); setFormError(t("signup.correctFields")); }
        else setFormError(err.message || t("signup.signupFailed"));
      } else { setFormError(tc("networkError")); }
    } finally { setSubmitting(false); }
  }

  return (
    <section className="mx-auto max-w-md">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">{t("signup.title")}</h1>
        <p className="mt-1 text-sm text-slate-600">{t("signup.subtitle")}</p>

        <div className="mt-6">
          <GoogleSignInButton label={tc("signUpWithGoogle")} />
          <div className="mt-3"><MicrosoftSignInButton label={tc("signUpWithMicrosoft")} /></div>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
          <div className="relative flex justify-center text-sm"><span className="bg-white px-3 text-slate-500">{tc("or")}</span></div>
        </div>

        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          {formError && (<div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</div>)}
          <FormField label={t("signup.email")} type="email" name="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fieldErrors.email} required />
          <FormField label={t("signup.password")} type="password" name="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint={t("signup.passwordHint")} error={fieldErrors.password} required />
          <FormField label={t("signup.confirmPassword")} type="password" name="confirm-password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={fieldErrors.confirm} required />
          <Button type="submit" loading={submitting} className="mt-2 w-full">{t("signup.submit")}</Button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          {t("signup.hasAccount")}{" "}
          <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-700">{t("signup.logIn")}</Link>
        </p>
      </div>
    </section>
  );
}
